import { useMemo } from 'react';
import { TREE_STATUS_TOTALS } from '../../../constants/queryKeys';
import useGraphqlRequestParams from '../../../hooks/useGraphqlRequestParams';
import type { FilterItem } from '../../../hooks/useUrlFilters';
import {
  enqueueTreeStatusTotalsBatch,
  type TreeStatusTotalsBatchContext,
  type TreeStatusTotalsBatchItem,
} from '../services/treeNodeTotalsBatcher';
import type { TreeTotalsType, TreeType, TreeViewBy } from '../../../types';
import {
  toGqlFilters,
  toGqlTreeCategory,
  toGqlTreeViewType,
} from '../../../utils/graphqlFilters';
import { useAsyncTreeNodeTotals } from './useAsyncTreeNodeTotals';

type Props = {
  category: TreeType;
  view: TreeViewBy;
  daysBack: number;
  filters: FilterItem[];
  kind: TreeTotalsType;
  requestKey: string;
  enabled: boolean;
};

export const useTreeNodeTotals = ({
  category,
  view,
  daysBack,
  filters,
  kind,
  requestKey,
  enabled,
}: Props) => {
  const { accessToken, environmentId, gqlEndpoint } = useGraphqlRequestParams(
    {},
  );

  const batchCtx: TreeStatusTotalsBatchContext | null = useMemo(() => {
    if (!accessToken || !environmentId || !gqlEndpoint) {
      return null;
    }

    return {
      gqlEndpoint,
      accessToken,
      category: toGqlTreeCategory(category),
      view: toGqlTreeViewType(view),
      daysBack,
      filters: toGqlFilters(filters),
    };
  }, [
    accessToken,
    category,
    daysBack,
    environmentId,
    filters,
    gqlEndpoint,
    view,
  ]);

  const batchItem: TreeStatusTotalsBatchItem = useMemo(() => {
    const requestId = `${kind}:${requestKey}`;
    if (kind === 'folder') {
      return { requestId, path: requestKey };
    }
    return { requestId, assetId: requestKey };
  }, [kind, requestKey]);

  return useAsyncTreeNodeTotals({
    queryKeyPrefix: TREE_STATUS_TOTALS,
    ctx: batchCtx,
    item: batchItem,
    enqueue: enqueueTreeStatusTotalsBatch,
    enabled: Boolean(
      enabled && batchCtx && requestKey && category && view && daysBack > 0,
    ),
    extraQueryKeyParts: [
      environmentId,
      batchCtx?.category,
      batchCtx?.view,
      daysBack,
      filters,
      kind,
      requestKey,
    ],
  });
};
